import { usePreferenceCenter } from '@church/data'
import { faBellSlash, faCircleCheck, faLinkSlash, faSliders } from '@church/ui-icons'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Button, Card, ErrorNote, Icon, Spinner } from '../components/ui.tsx'

/**
 * The footer's Unsubscribe link. One button, not a questionnaire: CAN-SPAM
 * allows no more than that, and a church should ask for less.
 * (Mail clients' one-click unsubscribe posts straight to the API.)
 */
export const Route = createFileRoute('/unsubscribe/$ref')({
  component: UnsubscribePage,
})

function UnsubscribePage() {
  const { ref } = Route.useParams()
  const { preferences, unsubscribe } = usePreferenceCenter(ref)
  const done = unsubscribe.isSuccess || preferences.data?.all === false
  return (
    <div className="min-h-screen bg-surface-base px-4 py-12">
      <Card className="mx-auto max-w-md space-y-4 p-6 text-center">
        {preferences.isPending && <Spinner />}
        {preferences.error && (
          <p className="flex items-center justify-center gap-2 text-content-default">
            <Icon icon={faLinkSlash} className="size-5 text-content-subtle" />
            This link has expired or is not valid.
          </p>
        )}
        {preferences.data && (
          <>
            <h1 className="text-xl font-bold text-content-default">
              {preferences.data.churchName}
            </h1>
            <Icon
              icon={done ? faCircleCheck : faBellSlash}
              className={`mx-auto size-10 ${done ? 'text-success-solid' : 'text-content-subtle'}`}
            />
            {done ? (
              <p className="text-content-default">
                You’re unsubscribed. You won’t receive emails or texts from us.
              </p>
            ) : (
              <>
                <p className="text-content-muted">Stop all emails and text messages from us?</p>
                <Button
                  variant="danger"
                  icon={faBellSlash}
                  onClick={() => unsubscribe.mutate()}
                  disabled={unsubscribe.isPending}
                >
                  Unsubscribe
                </Button>
              </>
            )}
            <Link
              to="/preferences/$ref"
              params={{ ref }}
              className="flex items-center justify-center gap-1.5 text-sm text-content-brand"
            >
              <Icon icon={faSliders} className="size-3.5" />
              {done ? 'Changed your mind? Manage preferences' : 'Or choose what you receive'}
            </Link>
            <ErrorNote error={unsubscribe.error} />
          </>
        )}
      </Card>
    </div>
  )
}
